import "./BoxScoreEvents.css";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ISoccerMatch,
  ISoccerMatchCardEvent,
  ISoccerMatchEvent,
  ISoccerMatchGoalEvent,
  ISoccerMatchMissedPenaltyEvent,
  ISoccerMatchPenaltyShotEvent,
  ISoccerMatchSubstituteEvent,
  SoccerMatchEventType,
} from "../../../../../../types";
import { useMatchResult, useMediaQuery } from "../../../../hooks";
import { MatchKeyEvent } from "../MatchKeyEvent";

const DEFAULT_EVENTS_TO_SHOW = 3;

export const BoxScoreEvents = ({
  soccerMatch,
}: {
  soccerMatch: ISoccerMatch;
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const { isPenaltyShootout, penaltyScoreboard, isMatchPlaying } =
    useMatchResult({
      soccerMatch,
    });

  const eventsList = useMemo(() => {
    return (soccerMatch?.events ?? []).slice().reverse();
  }, [soccerMatch?.events]);

  const teamId = {
    home: soccerMatch.contestants[0].id,
    away: soccerMatch.contestants[1].id,
  };

  const getEventKey = (event: ISoccerMatchEvent) => {
    switch (event.eventType) {
      case SoccerMatchEventType.CARD: {
        const { eventType, type, playerName, timestamp } =
          event as ISoccerMatchCardEvent;
        return `${eventType}-${type}-${playerName}-${timestamp}`;
      }
      case SoccerMatchEventType.MISSED_PENALTY: {
        const { eventType, playerName, timestamp } =
          event as ISoccerMatchMissedPenaltyEvent;
        return `${eventType}-${playerName}-${timestamp}`;
      }
      case SoccerMatchEventType.PENALTY_SHOT: {
        const { eventType, scorerName, timestamp } =
          event as ISoccerMatchPenaltyShotEvent;
        return `${eventType}-${scorerName}-${timestamp}`;
      }
      case SoccerMatchEventType.GOAL: {
        const { eventType, scorerName, timestamp } =
          event as ISoccerMatchGoalEvent;
        return `${eventType}-${scorerName}-${timestamp}`;
      }
      case SoccerMatchEventType.SUSTITUTE: {
        const { eventType, playerOnName, playerOffName, timestamp } =
          event as ISoccerMatchSubstituteEvent;
        return `${eventType}-${playerOnName}-${playerOffName}-${timestamp}`;
      }
      default: {
        return event.timestamp;
      }
    }
  };

  const showPenaltyScorecard = isPenaltyShootout && isMatchPlaying;
  const showAll = isExpanded || isDesktop;
  const visibleEvents = showAll
    ? eventsList
    : eventsList.slice(0, DEFAULT_EVENTS_TO_SHOW);
  // Desktop has no truncation, so no button there. On mobile, show the button
  // always (disabled when there's nothing to expand) so the layout is stable.
  const canExpand = eventsList.length > DEFAULT_EVENTS_TO_SHOW;

  return (
    <div className="box-score-events-container">
      {eventsList.length ? (
        <div className="box-score-events">
          {showPenaltyScorecard && (
            <div className="penalty-shootout-score">
              <p className="penalty-score-label">
                {t("boxScore.penaltyShootout")}
              </p>
              <div className="box-score-events-highlighted-content">
                {penaltyScoreboard.home}:{penaltyScoreboard.away}
              </div>
            </div>
          )}
          <div className="box-score-events-list">
            {visibleEvents.map((event) => (
              <MatchKeyEvent
                key={getEventKey(event)}
                teamId={teamId}
                event={event}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="no-events-label">{t("boxScore.keyMomentsDescription")}</p>
      )}
      {!isDesktop && (
        <button
          className={`expand-events-button${
            canExpand ? "" : " expand-events-button--inactive"
          }`}
          onClick={canExpand ? () => setIsExpanded(!isExpanded) : undefined}
          disabled={!canExpand}
          aria-disabled={!canExpand}
        >
          {isExpanded ? t("actions.collapse") : t("actions.expand")}
          <img
            src="/images/box-score/down-arrow.svg"
            height={12}
            width={12}
            alt="down-arrow"
            className={`box-score-event-arrow ${
              isExpanded ? "box-score-event-up-arrow" : ""
            }`}
          />
        </button>
      )}
    </div>
  );
};
